import React, { useState, useRef, useEffect, useCallback } from 'react';
import PDFFormFiller from './components/PDFFormFiller';
import { authFetch, fetchCsrfToken } from './authFetch';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

function FormsList({ customer }) {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [pdfTemplates, setPdfTemplates] = useState([]);
  const [filledForms, setFilledForms] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [selectedPdfTemplate, setSelectedPdfTemplate] = useState(null);
  const [selectedFilledForm, setSelectedFilledForm] = useState(null);
  const [completedPdfUrl, setCompletedPdfUrl] = useState(null);
  const modalRef = useRef(null);

  // Signing request state
  const [signingRequests, setSigningRequests] = useState([]);
  const [showSigningModal, setShowSigningModal] = useState(false);
  const [signingTemplate, setSigningTemplate] = useState(null);
  const [signingEmail, setSigningEmail] = useState('');
  const [signingName, setSigningName] = useState('');
  const [sendingSignature, setSendingSignature] = useState(false);
  const [signingError, setSigningError] = useState('');
  const [signingSuccess, setSigningSuccess] = useState('');

  // Fetch PDF templates and filled forms
  const fetchTemplatesAndForms = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      // Fetch active templates
      const templatesRes = await authFetch(`${BASE_URL}/api/form-templates/`);
      if (templatesRes.ok) {
        const data = await templatesRes.json();
        // Handle paginated response (DRF returns {results: [...]} or plain array)
        const templates = data.results || data || [];
        setPdfTemplates(templates.filter(t => t.is_active));
      }

      // Fetch filled forms for this customer
      if (customer?.id) {
        const formsRes = await authFetch(`${BASE_URL}/api/filled-forms/?customer_id=${customer.id}`);
        if (formsRes.ok) {
          const formsData = await formsRes.json();
          // Handle paginated response
          setFilledForms(formsData.results || formsData || []);
        }

        // Fetch signing requests for this customer
        try {
          const sigRes = await authFetch(`${BASE_URL}/api/signing/requests/?customer_id=${customer.id}`);
          if (sigRes.ok) {
            const sigData = await sigRes.json();
            setSigningRequests(sigData || []);
          }
        } catch (e) {
          // Signing feature may not be deployed yet
          console.error('Error fetching signing requests:', e);
        }
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  }, [customer?.id]);

  useEffect(() => {
    fetchTemplatesAndForms();
  }, [fetchTemplatesAndForms]);

  const handlePdfTemplateSelect = (template, existingFilledForm = null) => {
    setSelectedPdfTemplate(template);
    setSelectedFilledForm(existingFilledForm);
    setFormSubmitted(false);
    setCompletedPdfUrl(null);
    setShowModal(true);
  };

  const handlePdfFormComplete = (result, status) => {
    if (status === 'completed' && result.final_pdf_url) {
      setCompletedPdfUrl(result.final_pdf_url);
      setFormSubmitted(true);
    } else if (status === 'draft') {
      // Draft saved, close modal
      setShowModal(false);
      fetchTemplatesAndForms(); // Refresh list
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    if (formSubmitted) {
      setSelectedPdfTemplate(null);
      setSelectedFilledForm(null);
      setFormSubmitted(false);
      fetchTemplatesAndForms(); // Refresh list after form completion
    }
  };

  // Handle downloading PDF
  const handleDownloadPDF = () => {
    if (completedPdfUrl) {
      window.open(completedPdfUrl, '_blank');
    }
  };

  // Handle printing PDF
  const handlePrintPDF = () => {
    if (completedPdfUrl) {
      const printFrame = document.createElement('iframe');
      printFrame.style.display = 'none';
      document.body.appendChild(printFrame);

      printFrame.onload = () => {
        try {
          printFrame.contentWindow.print();
        } catch (error) {
          console.error('Print failed:', error);
          window.open(completedPdfUrl, '_blank');
        }
      };

      printFrame.src = completedPdfUrl;
    }
  };

  // Close modal when clicking outside
  const handleOutsideClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      handleCloseModal();
    }
  };

  // --- Send for Signature ---
  const openSigningModal = (template) => {
    setSigningTemplate(template);
    setSigningEmail(customer?.email || '');
    setSigningName(`${customer?.first_name || ''} ${customer?.last_name || ''}`.trim());
    setSigningError('');
    setSigningSuccess('');
    setShowSigningModal(true);
  };

  // Get draft data for a template (if agent already filled it out)
  const getDraftData = (templateId) => {
    const draft = filledForms.find(f => f.template === templateId && f.status === 'draft');
    return {
      form_data: draft?.form_data || {},
      signatures: draft?.signatures || {},
    };
  };

  const handleSendForSignature = async () => {
    if (!signingEmail.trim()) {
      setSigningError('Email address is required.');
      return;
    }
    setSendingSignature(true);
    setSigningError('');
    setSigningSuccess('');
    try {
      await fetchCsrfToken();
      // Include agent-entered form data and signatures from any existing draft
      const draftData = getDraftData(signingTemplate.id);
      const resp = await authFetch(`${BASE_URL}/api/signing/send/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          template_id: signingTemplate.id,
          customer_id: customer.id,
          client_email: signingEmail.trim(),
          client_name: signingName.trim(),
          form_data: draftData.form_data,
          signatures: draftData.signatures,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to send.');
      }
      const data = await resp.json();
      setSigningSuccess(
        data.email_sent_via
          ? `Sent via ${data.email_sent_via === 'graph' ? 'Outlook' : 'email'}!`
          : 'Signing request created (email delivery pending).'
      );
      // Refresh signing requests
      fetchTemplatesAndForms();
    } catch (e) {
      setSigningError(e.message);
    } finally {
      setSendingSignature(false);
    }
  };

  // Status badge colors
  const statusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-700';
      case 'viewed': return 'bg-blue-100 text-blue-700';
      case 'completed': return 'bg-green-100 text-green-700';
      case 'expired': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Find draft filled forms for templates
  const getDraftForTemplate = (templateId) => {
    return filledForms.find(f => f.template === templateId && f.status === 'draft');
  };

  const getCompletedFormsForTemplate = (templateId) => {
    return filledForms.filter(f => f.template === templateId && f.status === 'completed');
  };

  // Modal overlay for form display
  const FormModal = () => {
    if (!showModal) return null;

    // PDF Form Filler mode (full screen)
    if (selectedPdfTemplate && !formSubmitted) {
      return (
        <div className="fixed inset-0 bg-white z-50 flex flex-col">
          <PDFFormFiller
            template={selectedPdfTemplate}
            customer={customer}
            filledForm={selectedFilledForm}
            onComplete={handlePdfFormComplete}
            onCancel={handleCloseModal}
          />
        </div>
      );
    }

    return (
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center p-4 pt-24 overflow-auto"
        onClick={handleOutsideClick}
      >
        <div
          ref={modalRef}
          className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[80vh] overflow-auto my-8 p-6 relative"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={handleCloseModal}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
            aria-label="Close form"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {formSubmitted ? (
            <div className="bg-green-100 p-4 rounded border border-green-300 mb-4">
              <h3 className="text-lg font-semibold text-green-700">Form Submitted Successfully!</h3>
              <p className="mt-2">The form has been saved for this customer.</p>

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  onClick={handleCloseModal}
                  className="bg-blue-500 text-white px-4 py-2 rounded"
                >
                  Close
                </button>

                {completedPdfUrl && (
                  <>
                    <button
                      onClick={handleDownloadPDF}
                      className="bg-green-500 text-white px-4 py-2 rounded flex items-center"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download PDF
                    </button>

                    <button
                      onClick={handlePrintPDF}
                      className="bg-purple-500 text-white px-4 py-2 rounded flex items-center"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                      </svg>
                      Print
                    </button>
                  </>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div className="forms-container">
      <h3 className="text-lg font-semibold mb-4">Available Forms</h3>

      {/* PDF Templates Section */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-600 mb-2">PDF Form Templates</h4>

        {loadingTemplates ? (
          <div className="text-gray-500 text-sm">Loading templates...</div>
        ) : pdfTemplates.length === 0 ? (
          <div className="text-gray-500 text-sm border border-dashed p-4 rounded">
            No PDF templates available. Admins can add templates from the PDF Templates admin page.
          </div>
        ) : (
          <div className="grid gap-3">
            {pdfTemplates.map(template => {
              const draft = getDraftForTemplate(template.id);
              const completed = getCompletedFormsForTemplate(template.id);

              return (
                <div
                  key={template.id}
                  className="border p-4 rounded hover:bg-gray-50 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-medium">{template.name}</h4>
                      {template.description && (
                        <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                      )}

                      {/* Draft indicator */}
                      {draft && (
                        <div className="mt-2 text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded inline-block">
                          Draft in progress - Last saved {new Date(draft.updated_at).toLocaleDateString()}
                        </div>
                      )}

                      {/* Completed forms */}
                      {completed.length > 0 && (
                        <div className="mt-2 text-xs text-green-700">
                          {completed.length} completed form(s)
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 ml-4">
                      {draft ? (
                        <button
                          onClick={() => handlePdfTemplateSelect(template, draft)}
                          className="bg-yellow-500 text-white px-3 py-1 rounded text-sm hover:bg-yellow-600"
                        >
                          Continue Draft
                        </button>
                      ) : (
                        <button
                          onClick={() => handlePdfTemplateSelect(template)}
                          className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                        >
                          Fill Out
                        </button>
                      )}
                      <button
                        onClick={() => openSigningModal(template)}
                        className="bg-purple-600 text-white px-3 py-1 rounded text-sm hover:bg-purple-700"
                        title="Send to client for electronic signature"
                      >
                        Send for Signature
                      </button>
                    </div>
                  </div>

                  {/* List completed forms with download links */}
                  {completed.length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-xs font-medium text-gray-600 mb-2">Completed Submissions:</p>
                      <div className="space-y-1">
                        {completed.map(form => (
                          <div key={form.id} className="flex items-center justify-between text-sm">
                            <span className="text-gray-600">
                              {new Date(form.completed_at).toLocaleDateString()} at {new Date(form.completed_at).toLocaleTimeString()}
                            </span>
                            {form.final_pdf_url && (
                              <a
                                href={form.final_pdf_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline text-xs"
                              >
                                Download PDF
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Completed Forms from Inactive Templates */}
      {(() => {
        // Find completed forms whose templates are not in the active list
        const activeTemplateIds = pdfTemplates.map(t => t.id);
        const formsFromInactiveTemplates = filledForms.filter(
          f => f.status === 'completed' && !activeTemplateIds.includes(f.template)
        );

        if (formsFromInactiveTemplates.length === 0) return null;

        return (
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-600 mb-2">Previously Completed Forms</h4>
            <div className="border border-gray-200 rounded p-4 bg-gray-50">
              <p className="text-xs text-gray-500 mb-3">
                Forms from templates that are no longer active
              </p>
              <div className="space-y-2">
                {formsFromInactiveTemplates.map(form => (
                  <div key={form.id} className="flex items-center justify-between text-sm bg-white p-2 rounded border">
                    <div>
                      <span className="font-medium">{form.template_name || 'Unknown Template'}</span>
                      <span className="text-gray-500 ml-2">
                        - {new Date(form.completed_at).toLocaleDateString()}
                      </span>
                    </div>
                    {form.final_pdf_url && (
                      <a
                        href={form.final_pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline text-xs"
                      >
                        Download PDF
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Pending Signatures Section */}
      {signingRequests.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-600 mb-2">Pending Signatures</h4>
          <div className="space-y-2">
            {signingRequests.map(req => (
              <div key={req.id} className="border rounded p-3 flex items-center justify-between bg-white">
                <div>
                  <span className="font-medium text-sm">{req.template_name}</span>
                  <span className="text-gray-500 text-sm ml-2">
                    &rarr; {req.client_email}
                  </span>
                  <div className="text-xs text-gray-400 mt-1">
                    Sent {new Date(req.sent_at).toLocaleDateString()}
                    {req.email_sent_via && ` via ${req.email_sent_via === 'graph' ? 'Outlook' : 'SES'}`}
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded font-medium ${statusColor(req.status)}`}>
                  {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Render the form filler modal */}
      <FormModal />

      {/* Send for Signature Modal */}
      {showSigningModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold mb-4">
              Send for Signature
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Send <strong>{signingTemplate?.name}</strong> to the client for electronic signature.
            </p>

            {signingError && (
              <div className="bg-red-50 text-red-700 p-2 rounded text-sm mb-3">{signingError}</div>
            )}
            {signingSuccess && (
              <div className="bg-green-50 text-green-700 p-2 rounded text-sm mb-3">{signingSuccess}</div>
            )}

            {!signingSuccess && (
              <>
                <div className="mb-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client Email</label>
                  <input
                    type="email"
                    value={signingEmail}
                    onChange={e => setSigningEmail(e.target.value)}
                    className="w-full border rounded px-3 py-2 text-sm"
                    placeholder="client@example.com"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
                  <input
                    type="text"
                    value={signingName}
                    onChange={e => setSigningName(e.target.value)}
                    className="w-full border rounded px-3 py-2 text-sm"
                    placeholder="John Smith"
                  />
                </div>
              </>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowSigningModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm"
              >
                {signingSuccess ? 'Close' : 'Cancel'}
              </button>
              {!signingSuccess && (
                <button
                  onClick={handleSendForSignature}
                  disabled={sendingSignature}
                  className={`px-4 py-2 rounded text-white text-sm ${
                    sendingSignature ? 'bg-gray-400 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700'
                  }`}
                >
                  {sendingSignature ? 'Sending...' : 'Send'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FormsList;
